// Revisado: 2025-12-24 [I18n Scan]
import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { API_BASE_URL } from '../../../constants';

const SupportTable = ({
    filteredData = [], // Default to empty array for safety
    selectedEffect,
    loading
}) => {
    const { t } = useTranslation();

    // Sorting State
    const [sortConfig, setSortConfig] = useState(null); // { key: 'name' | 0-4, direction: 'asc' | 'desc' }

    // Reset sort when effect changes
    useEffect(() => {
        setSortConfig(null);
    }, [selectedEffect]);

    // 1. Mandatory Check
    if (!selectedEffect) {
        return (
            <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-12 text-center">
                <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{t('SupportTable.empty_title')}</h3>
                <p className="text-slate-400">
                    <Trans i18nKey="SupportTable.empty_desc" components={{ strong0: <strong /> }} />
                </p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="text-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
                <p className="text-slate-400">{t('SupportTable.loading')}</p>
            </div>
        );
    }

    // Clone data for sorting to avoid mutating prop
    const sortedCards = [...filteredData];

    // 2. Sorting Logic
    if (sortConfig) {
        sortedCards.sort((a, b) => {
            const { key, direction } = sortConfig;
            const isAsc = direction === 'asc';

            if (key === 'name') {
                const nameA = a.info.char_name || a.info.name || '';
                const nameB = b.info.char_name || b.info.name || '';
                return isAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
            } else if (typeof key === 'number') {
                // Use pre-calculated values
                const valA = a.values[key] || 0;
                const valB = b.values[key] || 0;
                return isAsc ? valA - valB : valB - valA;
            }
            return 0;
        });
    } else {
        // Default: Sort by Max Value of the effect (using values array max)
        sortedCards.sort((a, b) => {
            const valA = Math.max(...(a.values || [0]));
            const valB = Math.max(...(b.values || [0]));
            return valB - valA;
        });
    }

    const handleSort = (key) => {
        let direction = 'desc';
        if (key === 'name') direction = 'asc';

        if (sortConfig && sortConfig.key === key) {
            direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        }
        setSortConfig({ key, direction });
    };

    // Helper for Type Icon path (Unchanged)
    const getTypeIconPath = (typeStr) => {
        if (!typeStr) return '';
        const type = typeStr.charAt(0).toUpperCase() + typeStr.slice(1).toLowerCase();
        if (['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(type)) {
            return `/images/Icon/Stats_icons/Stat_Type_${type}.png`;
        } else if (['Pal', 'Group'].includes(type)) {
            return `/images/Icon/Support_Icons/Support_Type_${type}.png`;
        }
        return '';
    };

    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) {
            // Inactive state
            return <span className="opacity-20 hover:opacity-100 transition-opacity">⇅</span>;
        }
        return sortConfig.direction === 'asc'
            ? <span className="text-cyan-500 dark:text-cyan-400">▲</span>
            : <span className="text-cyan-500 dark:text-cyan-400">▼</span>;
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                    <span className="text-cyan-600 dark:text-cyan-400">{t('SupportTable.header_comparing')}</span> {selectedEffect}
                </h3>
                <span className="text-sm text-slate-500">{t('SupportTable.count_cards', { count: sortedCards.length })}</span>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
                            {/* Support Card Column Sort */}
                            <th className="p-4 border-b border-slate-200 dark:border-slate-700 w-64 sticky left-0 bg-slate-100 dark:bg-slate-900 z-10">
                                <button
                                    onClick={() => handleSort('name')}
                                    className="flex items-center gap-2 hover:text-slate-800 dark:hover:text-white transition-colors group focus:outline-none"
                                >
                                    <span>{t('SupportTable.col_support')}</span>
                                    {getSortIcon('name')}
                                </button>
                            </th>

                            {/* Cap Columns Sort */}
                            {[0, 1, 2, 3, 4].map(cap => (
                                <th key={cap} className="p-4 border-b border-slate-200 dark:border-slate-700 text-center min-w-[80px]">
                                    <div className="flex flex-col items-center gap-1">
                                        <button
                                            onClick={() => handleSort(cap)}
                                            className="flex flex-col items-center gap-1 hover:brightness-125 transition-all focus:outline-none w-full"
                                            title={t('SupportTable.tooltip_sort')}
                                        >
                                            {/* Crystal Visualization */}
                                            <div className="flex items-center justify-center h-8">
                                                {cap === 0 ? (
                                                    <img src="/images/Icon/Resources/CrystalCap.png" className="w-6 opacity-30 grayscale" alt="0" />
                                                ) : (
                                                    <div className="flex items-center">
                                                        <img src="/images/Icon/Resources/CrystalCap.png" className="w-6" alt="Cap" />
                                                        {cap > 1 && <span className="text-[10px] font-bold ml-0.5 text-slate-600 dark:text-slate-400">x{cap}</span>}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Sort Icon helper below crystal */}
                                            <div className="h-4 flex items-center justify-center">
                                                {getSortIcon(cap)}
                                            </div>
                                        </button>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="text-slate-800 dark:text-white divide-y divide-slate-200 dark:divide-slate-700">
                        {sortedCards.map((card) => {
                            // Pre-computed values from parent
                            // card.values = [v0, v1...], card.uniqueBonusVal = X
                            const uniqueBonusVal = card.uniqueBonusVal || 0;
                            const hasMatchingUnique = uniqueBonusVal > 0;

                            return (
                                <tr key={card.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group">
                                    {/* Support Card Column */}
                                    <td className="p-3 border-r border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-800/50 sticky left-0 z-10 group-hover:bg-slate-50 dark:group-hover:bg-slate-700">
                                        <a
                                            href={`https://umacalculator.com/characters/support/${card.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                                            title={t('SupportTable.tooltip_link')}
                                        >
                                            {/* Icon with Overlay */}
                                            <div className="relative w-12 h-12 flex-shrink-0">
                                                <img
                                                    src={`${API_BASE_URL}/api/thumb?img=/images/Icon/Support_Character_Icon/Support_Card_${card.id}_Icon.png&width=64`}
                                                    className="w-full h-full rounded shadow-sm object-cover"
                                                    alt={card.info.name}
                                                    loading="lazy"
                                                />
                                                <img
                                                    src={getTypeIconPath(card.info.Support_Type)}
                                                    className="absolute -bottom-1 -right-1 w-5 h-5 z-10 drop-shadow-md"
                                                    alt={card.info.Support_Type}
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-bold text-sm truncate w-32 md:w-40 text-cyan-600 dark:text-cyan-100">
                                                    {card.info.char_name || card.info.name}
                                                </div>
                                                <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate w-32 md:w-40">
                                                    {card.info.card_name}
                                                </div>
                                                <div className="text-[9px] text-slate-500 capitalize">{card.info.rarity} • {card.info.Support_Type}</div>
                                            </div>
                                        </a>
                                    </td>

                                    {/* Cap Value Columns */}
                                    {[0, 1, 2, 3, 4].map((capIndex) => {
                                        // Use pre-calculated values
                                        const totalVal = card.values[capIndex];

                                        // Handle NULL values (Inventory Filter Masking)
                                        if (totalVal === null) {
                                            return (
                                                <td key={capIndex} className="p-3 text-center border-l border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/20">
                                                    <span className="text-slate-300 dark:text-slate-600 font-mono text-xs">-</span>
                                                </td>
                                            );
                                        }

                                        // Base val can be derived if needed, but we mostly just show total
                                        const baseVal = totalVal - uniqueBonusVal;

                                        // If baseVal is 0 and no unique (and total is 0), show -
                                        // Note: totalVal is guaranteed to be number here.
                                        if (totalVal === 0 && !hasMatchingUnique) {
                                            return (
                                                <td key={capIndex} className="p-3 text-center text-slate-400 dark:text-slate-600">-</td>
                                            );
                                        }

                                        // Display formatted
                                        return (
                                            <td key={capIndex} className="p-3 text-center font-mono">
                                                <div className="flex flex-col items-center justify-center">
                                                    {/* Total Value */}
                                                    <span className={`text-lg font-bold ${hasMatchingUnique ? 'text-amber-600 dark:text-amber-300' : 'text-slate-800 dark:text-white'}`}>
                                                        {Number.isInteger(totalVal) ? totalVal : totalVal.toFixed(1)}
                                                    </span>

                                                    {/* Breakdown if Unique Active */}
                                                    {hasMatchingUnique && uniqueBonusVal > 0 && (
                                                        <span className="text-[10px] text-amber-600/80 dark:text-amber-500/80">
                                                            {Number.isInteger(baseVal) ? baseVal : baseVal.toFixed(1)}(+{Number.isInteger(uniqueBonusVal) ? uniqueBonusVal : uniqueBonusVal.toFixed(1)})
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}

                        {sortedCards.length === 0 && (
                            <tr>
                                <td colSpan="6" className="p-8 text-center text-slate-500">
                                    {t('SupportTable.no_results', { effect: selectedEffect })}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SupportTable;
